import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import {
  archiveProject,
  deleteProject,
  listProjects,
  type ProjectSummary,
} from "@/api/projects";
import { Button, Spinner, Alert, cx } from "@/components/ui";

const STATUS_TONE: Record<string, string> = {
  draft: "bg-zinc-100 text-zinc-600",
  ready: "bg-blue-100 text-blue-700",
  running: "bg-amber-100 text-amber-700",
  complete: "bg-emerald-100 text-emerald-700",
  failed: "bg-rose-100 text-rose-700",
};

// Per-status tile background + glyph — gives each card a visual anchor.
const STATUS_TILE: Record<string, string> = {
  draft: "bg-zinc-100",
  ready: "bg-blue-100",
  running: "bg-amber-100",
  complete: "bg-emerald-100",
  failed: "bg-rose-100",
};
const STATUS_GLYPH: Record<string, string> = {
  draft: "📝",
  ready: "📂",
  running: "⏳",
  complete: "✅",
  failed: "⚠️",
};

export function ProjectsListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [showArchived, setShowArchived] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ["projects", showArchived],
    queryFn: () => listProjects(showArchived),
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ["projects"] });

  const archive = useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) => archiveProject(id, archived),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (id: string) => deleteProject(id),
    onSuccess: refresh,
  });

  return (
    <div className="space-y-6">
      {/* Header — stacks on mobile so the description + controls don't collide. */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-ink sm:text-3xl">{t("Projects")}</h1>
          <p className="mt-1 max-w-xl text-sm text-ink-soft">
            {t("Upload course material plus an assignment. An agent reads it, writes a recap, solves the task, and builds a trainable set.")}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={() => setShowArchived((v) => !v)}
            className={cx(
              "rounded-full border px-3 py-1.5 text-sm font-medium transition",
              showArchived
                ? "border-brand-500 bg-brand-50 text-brand-700"
                : "border-black/10 text-ink-soft hover:border-black/20",
            )}
          >
            {showArchived ? t("Showing archived") : t("Show archived")}
          </button>
          <Button onClick={() => navigate("/app/projects/new")}>{t("New project")}</Button>
        </div>
      </div>

      {isLoading && (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      )}
      {error && <Alert>{t("Could not load projects.")}</Alert>}

      {data && data.length === 0 && (
        <div className="card flex flex-col items-center gap-3 py-14 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-3xl">
            📂
          </div>
          <p className="text-ink-soft">{showArchived ? t("No archived projects.") : t("No projects yet.")}</p>
          {!showArchived && (
            <Button onClick={() => navigate("/app/projects/new")}>{t("Create your first project")}</Button>
          )}
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {data?.map((p) => (
          <ProjectCard
            key={p.id}
            project={p}
            busy={archive.isPending || remove.isPending}
            onArchive={(archived) => archive.mutate({ id: p.id, archived })}
            onDelete={() => {
              if (confirm(t("Delete “{{title}}”? This cannot be undone.", { title: p.title }))) remove.mutate(p.id);
            }}
          />
        ))}
      </div>
    </div>
  );
}

function ProjectCard({
  project,
  busy,
  onArchive,
  onDelete,
}: {
  project: ProjectSummary;
  busy: boolean;
  onArchive: (archived: boolean) => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  const archived = !!project.archived_at;
  const to = `/app/projects/${project.id}`;
  return (
    <div
      className={cx(
        "card group flex flex-col p-4 transition hover:-translate-y-0.5 hover:shadow-glow",
        archived && "opacity-70",
      )}
    >
      <Link to={to} className="flex flex-1 flex-col">
        <div className="flex items-start gap-3">
          <div
            className={cx(
              "flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-xl",
              STATUS_TILE[project.status] ?? "bg-zinc-100",
            )}
          >
            {STATUS_GLYPH[project.status] ?? "📂"}
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="truncate font-semibold text-ink group-hover:text-brand-700">
              {project.title}
            </h3>
            <p className="mt-0.5 text-xs text-ink-faint">
              {t("{{count}} files", { count: project.file_count })}
            </p>
          </div>
          <span
            className={cx(
              "shrink-0 rounded-full px-2 py-0.5 text-xs font-medium capitalize",
              STATUS_TONE[project.status] ?? "bg-zinc-100 text-zinc-600",
            )}
          >
            {t(project.status)}
          </span>
        </div>
        {project.produced_question_set_id && (
          <p className="mt-3 inline-flex w-fit items-center gap-1 rounded-md bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
            {t("✓ Training set ready")}
          </p>
        )}
      </Link>
      <div className="mt-3 flex items-center gap-3 border-t border-black/5 pt-2 text-xs">
        <button
          type="button"
          disabled={busy}
          onClick={() => onArchive(!archived)}
          className="font-medium text-ink-soft hover:text-ink"
        >
          {archived ? t("Restore") : t("Archive")}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={onDelete}
          className="font-medium text-rose-600 hover:text-rose-700"
        >
          {t("Delete")}
        </button>
        <Link to={to} className="ml-auto font-medium text-brand-600 hover:text-brand-700">
          {t("Open →")}
        </Link>
      </div>
    </div>
  );
}
